import React from 'react';

const NapoleonicWarsPage = () => {
  const timeline = [
    { year: '1803', event: 'War Resumes', description: 'After a brief peace, Britain declares war on France, beginning the Napoleonic Wars proper.' },
    { year: '1804', event: 'Napoleon Crowned Emperor', description: 'Napoleon Bonaparte crowns himself Emperor of the French, consolidating his power.' },
    { year: '1805', event: 'Battle of Austerlitz', description: 'Napoleon\'s greatest victory - defeats combined Austrian and Russian forces in the "Battle of the Three Emperors."' },
    { year: '1805', event: 'Battle of Trafalgar', description: 'British naval victory under Admiral Nelson ensures British naval supremacy but Nelson is killed in action.' },
    { year: '1807', event: 'Continental System', description: 'Napoleon attempts to cripple Britain economically through a continent-wide blockade.' },
    { year: '1808-14', event: 'Peninsular War', description: 'French forces struggle against Spanish guerrillas and British troops in a prolonged conflict.' },
    { year: '1812', event: 'Invasion of Russia', description: 'Napoleon\'s Grande Armée of 600,000 men invades Russia; only a fraction survive the disastrous winter retreat.' },
    { year: '1813', event: 'Battle of Leipzig', description: 'The "Battle of Nations" - decisive Coalition victory that forces Napoleon back to France.' },
    { year: '1814', event: 'First Abdication', description: 'Napoleon abdicates and is exiled to Elba; Louis XVIII restored to French throne.' },
    { year: '1815', event: 'The Hundred Days', description: 'Napoleon escapes Elba and returns to power for 100 days before final defeat at Waterloo.' },
    { year: '1815', event: 'Battle of Waterloo', description: 'Napoleon\'s final defeat by Wellington and Blücher; exiled to remote island of Saint Helena.' }
  ];

  const keyFacts = [
    { icon: '👑', title: 'Napoleon Bonaparte', fact: 'Rose from Corsican artillery officer to Emperor of France and master of most of Europe' },
    { icon: '⚔️', title: 'Major Battles', fact: 'Over 60 major battles fought across Europe, from Spain to Russia, Egypt to Austria' },
    { icon: '🌍', title: 'Empire at Peak', fact: 'French Empire controlled most of continental Europe by 1812, with over 70 million subjects' },
    { icon: '📄', title: 'Napoleonic Code', fact: 'Revolutionary legal framework that still influences civil law systems in many countries today' },
    { icon: '❄️', title: 'Russian Campaign', fact: 'Of 600,000 soldiers who invaded Russia, fewer than 100,000 returned - a catastrophic loss' },
    { icon: '🏛️', title: 'Congress of Vienna', fact: 'Post-war peace conference redrew the map of Europe and established balance of power for decades' }
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="napoleonic-page">
      {/* Hero Section */}
      <div 
        className="relative h-96 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://cache.getarchive.net/Prod/thumb/cdn2/L3Bob3RvLzE4NTAvMTIvMzEvZnJhbmNvaXMtcGFzY2FsLXNpbW9uLWdlcmFyZC1sYS1iYXRhaWxsZS1kYXVzdGVybGl0ei0yLWRlY2VtYnJlLTE4MDUtNTNiYzIxLTEwMjQuanBn/320/143/jpg)'
        }}
      >
        <div className="hero-overlay absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-6">
            <h1 className="text-5xl md:text-7xl font-bold mb-4 text-amber-400" data-testid="page-title">
              Napoleonic Wars
            </h1>
            <p className="text-2xl text-amber-100 font-semibold">1803 - 1815</p>
            <p className="text-xl text-amber-200 mt-4 italic">"The Age of Napoleon"</p>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-stone-800 mb-6" data-testid="overview-title">Overview</h2>
          <div className="section-divider"></div>
          <p className="text-xl text-stone-700 leading-relaxed mb-6">
            The Napoleonic Wars were a series of major conflicts lasting from 1803 to 1815, pitting the French Empire and its allies, led by Napoleon Bonaparte, against various coalitions of European powers. These wars were a continuation of the conflicts sparked by the French Revolution and represented a clash between revolutionary republican ideals and traditional European monarchies.
          </p>
          <p className="text-xl text-stone-700 leading-relaxed mb-6">
            Napoleon\'s military genius and France\'s revolutionary fervor allowed him to conquer much of Europe, creating the largest European empire since Rome. His campaigns revolutionized military tactics and strategy, introduced the Napoleonic Code which modernized legal systems, and spread revolutionary principles across the continent.
          </p>
          <p className="text-xl text-stone-700 leading-relaxed">
            The wars finally ended with Napoleon\'s defeat at Waterloo in 1815. The Congress of Vienna subsequently redrew the map of Europe and established a balance of power that would last until World War I. The Napoleonic era fundamentally transformed Europe, spreading nationalism, liberalism, and modern state institutions that continue to influence the world today.
          </p>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="bg-stone-100 py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-stone-800 mb-12 text-center" data-testid="timeline-title">
            Timeline of Major Events
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="timeline-line pl-12 space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className="relative" data-testid={`timeline-event-${index}`}>
                  <div className="timeline-dot"></div>
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-2xl font-bold text-amber-600">{item.year}</h3>
                    </div>
                    <h4 className="text-xl font-bold text-stone-800 mb-2">{item.event}</h4>
                    <p className="text-stone-600 text-lg leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Key Facts Section */}
      <div className="container mx-auto px-6 py-16">
        <h2 className="text-4xl font-bold text-stone-800 mb-12 text-center" data-testid="facts-title">
          Key Facts & Figures
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {keyFacts.map((fact, index) => (
            <div 
              key={index} 
              className="bg-white p-6 rounded-lg shadow-lg historical-border hover:shadow-xl transition-shadow"
              data-testid={`fact-card-${index}`}
            >
              <div className="text-4xl mb-4">{fact.icon}</div>
              <h3 className="text-xl font-bold text-amber-600 mb-3">{fact.title}</h3>
              <p className="text-stone-700 text-lg leading-relaxed">{fact.fact}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Coalition Wars */}
      <div className="bg-stone-100 py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-stone-800 mb-12 text-center">The Seven Coalitions</h2>
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold text-amber-600 mb-3">First through Third Coalitions (1792-1805)</h3>
              <p className="text-stone-700 text-lg leading-relaxed">
                Early coalitions of European monarchies attempting to contain Revolutionary France. Napoleon emerged victorious, culminating in the spectacular triumph at Austerlitz.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold text-amber-600 mb-3">Fourth through Sixth Coalitions (1806-1814)</h3>
              <p className="text-stone-700 text-lg leading-relaxed">
                Period of French dominance over Europe, but the disastrous Russian campaign of 1812 marked the beginning of Napoleon\'s decline. The Sixth Coalition finally forced his first abdication.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold text-amber-600 mb-3">Seventh Coalition (1815)</h3>
              <p className="text-stone-700 text-lg leading-relaxed">
                Formed after Napoleon\'s escape from Elba during the Hundred Days. Ended with his final defeat at Waterloo and permanent exile to Saint Helena.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Causes & Consequences */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold text-stone-800 mb-6" data-testid="causes-title">Main Causes</h2>
            <div className="section-divider"></div>
            <ul className="space-y-4 mt-6">
              <li className="flex items-start">
                <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                <span className="text-lg text-stone-700">Continuation of French Revolutionary Wars and ideological conflict</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                <span className="text-lg text-stone-700">Napoleon\'s ambition to dominate Europe and spread French influence</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                <span className="text-lg text-stone-700">European monarchies\' fear of revolutionary ideals and French power</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                <span className="text-lg text-stone-700">British-French rivalry for global supremacy and trade dominance</span>
              </li>
            </ul>
          </div>
          <div>
            <h2 className="text-3xl font-bold text-stone-800 mb-6" data-testid="consequences-title">Consequences</h2>
            <div className="section-divider"></div>
            <ul className="space-y-4 mt-6">
              <li className="flex items-start">
                <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                <span className="text-lg text-stone-700">Congress of Vienna established new European order and balance of power</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                <span className="text-lg text-stone-700">Spread of nationalism across Europe, leading to unification movements</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                <span className="text-lg text-stone-700">Napoleonic Code modernized legal systems in many countries</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                <span className="text-lg text-stone-700">Britain emerged as the dominant world power for the next century</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                <span className="text-lg text-stone-700">Revolutionary ideas of liberty and equality spread throughout Europe</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Napoleon's Legacy */}
      <div className="bg-stone-800 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-amber-400 mb-6">Napoleon\'s Legacy</h2>
            <div className="section-divider"></div>
            <p className="text-xl text-amber-100 leading-relaxed mt-8">
              "Napoleon Bonaparte remains one of history\'s most controversial and influential figures. Though ultimately defeated, his impact on European politics, law, military strategy, and culture endures to this day. The Napoleonic Code, his military innovations, and the spread of revolutionary ideals fundamentally transformed the modern world."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NapoleonicWarsPage;